import * as React from 'react';
import * as Yup from 'yup';
import { Field, Formik, FieldProps, FormikProps, FormikValues } from 'formik';
import { omit } from 'lodash';
import {
  StyledForm,
  FieldRow,
  StyledFormGroup,
  StyledFormSelect,
  Card,
  Button,
  HeroImage,
  Box,
} from '../../components';
import { formatFormValues, formatGqlError, getLoggedInUser } from '../../utils/helpers';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { space } from 'styled-system';
import { images } from '../../theme';
import { StyledSystemProps } from '../../theme/StyledSystemProps';
import { H4 } from '../../typography';
import { UserRole } from '../../generated/graphql';
import { SelectObject, ImageObject } from '../../utils/types';
import { MutationFn } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import { USER_STATUS, USER_ROLES } from '../../utils/enums';

const CreateStakeholderValidation = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Required'),
  role: Yup.mixed().required('Required'),
  status: Yup.mixed().required('Required'),
});

type ProfileFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  type?: SelectObject[];
  status?: SelectObject[];
  uniqueID?: string;
};

type Props = RouteComponentProps & {
  mutation: MutationFn;
  error?: ApolloError;
  buttonText?: string;
  loading?: boolean;
  initialValues?: FormikValues;
  image?: ImageObject;
};

const Image = styled.img<StyledSystemProps>`
  ${space};
  max-width: 100%;
  width: 250px;
  height: auto;
  display: inline-block;
`;

class ProfileForm extends React.Component<Props> {
  renderQrCode = () => {
    switch (window.location.hostname) {
      case 'admin.steyncity.realist.co.za':
        return <Image mb={3} src={images.productionQr} />;
      case 'uat.admin.steyncity.realist.co.za':
        return <Image mb={3} src={images.stagingQr} />;
      default:
        return <Image mb={3} src={images.developQr} />;
    }
  };

  render() {
    const user = getLoggedInUser();
    const userRole = user && user.role;
    const { initialValues, mutation, loading, error, image } = this.props;
    return (
      <Formik
        validationSchema={CreateStakeholderValidation}
        initialValues={
          initialValues
            ? initialValues
            : {
                firstName: '',
                lastName: '',
                id: '',
                email: '',
                role: '',
                status: '',
              }
        }
        onSubmit={(values: ProfileFormValues) => {
          const formattedValues = formatFormValues(values);
          const cleanValues = omit(formattedValues, ['vendor.name', 'image']);
          mutation({
            variables: cleanValues,
          });
        }}
        render={(formikProps: FormikProps<ProfileFormValues>) => {
          return (
            <StyledForm flexDirection="row" flexWrap="nowrap">
              <Box flex={3} flexDirection="column-reverse">
                <Card mt={3}>
                  <H4 fontWeight="lighter" mb={3}>
                    Scan the QR code below to open the app in Expo.
                  </H4>
                  {this.renderQrCode()}
                </Card>
                <Card flex={3} bg="white" p={4}>
                  <FieldRow>
                    <Field
                      name="firstName"
                      render={({ field }: FieldProps<ProfileFormValues>) => (
                        <StyledFormGroup
                          {...field}
                          mr={[0, 0, 4]}
                          field={field}
                          form={formikProps}
                          label="First Name"
                        />
                      )}
                    />
                    <Field
                      name="lastName"
                      render={({ field }: FieldProps<ProfileFormValues>) => (
                        <StyledFormGroup {...field} field={field} form={formikProps} label="Last Name" />
                      )}
                    />
                  </FieldRow>
                  <FieldRow>
                    <Field
                      name="email"
                      render={({ field }: FieldProps<ProfileFormValues>) => (
                        <StyledFormGroup {...field} field={field} mr={[0, 0, 4]} form={formikProps} label="Email" />
                      )}
                    />
                    <Field
                      name="status"
                      render={({ field }: FieldProps<ProfileFormValues>) => (
                        <StyledFormSelect
                          {...field}
                          field={field}
                          mr={[0, 0, 4]}
                          label="Status"
                          form={formikProps}
                          options={USER_STATUS}
                        />
                      )}
                    />
                    <Field
                      name="role"
                      render={({ field }: FieldProps<ProfileFormValues>) => (
                        <StyledFormSelect
                          disabled={initialValues && initialValues.role.value !== 'SUPERADMIN'}
                          {...field}
                          field={field}
                          label="User Role"
                          form={formikProps}
                          options={USER_ROLES}
                        />
                      )}
                    />
                  </FieldRow>
                </Card>
              </Box>
              <Card ml={[0, 0, 4]} flex={1} bg="white" p={4}>
                <HeroImage src={image && image.scaled} type="profile" id={initialValues && initialValues.id} />
                {(userRole === UserRole.VendorAdmin || userRole === UserRole.VendorUser) && (
                  <Field
                    name="vendor.name"
                    render={({ field }: FieldProps<ProfileFormValues>) => (
                      <StyledFormGroup
                        {...field}
                        field={field}
                        form={formikProps}
                        label="Vendor Name"
                        disabled={true}
                      />
                    )}
                  />
                )}
                <Field
                  name="id"
                  render={({ field }: FieldProps<ProfileFormValues>) => (
                    <StyledFormGroup
                      {...field}
                      field={field}
                      form={formikProps}
                      pb={4}
                      label="Unique ID"
                      placeholder="Auto generated unique ID"
                      disabled={true}
                    />
                  )}
                />
                {error && formatGqlError(error)}
                <Button
                  mb={2}
                  type="button"
                  bg="coal"
                  onClick={() => this.props.history.push('/forgot-password')}
                  text="UPDATE PASSWORD"
                  isLoading={loading}
                />
                <Button mb={1} type="submit" text="SAVE & UPDATE" isLoading={loading} />
              </Card>
            </StyledForm>
          );
        }}
      />
    );
  }
}

export default withRouter(ProfileForm);



// WEBPACK FOOTER //
// ./src/forms/user/ProfileForm.tsx